/**
 * bulletin-api — 불레틴 화면(제품 등록 ③)의 API.
 *
 * 백엔드 `/api/admin/bulletin` (hcltech-parts `Bulletin.Routes.ts`).
 * 응답 키는 DB 열 그대로(snake_case). 화면 열 열 = 백엔드 `BULLETIN_DOC_VIEW_COLUMNS`. 목록은 한 묶음(BulletinDocViewList)으로 온다.
 *
 * 1 타입 → 2 시그니처 → 3 몸통(이 커밋).
 */
import { z } from 'zod';
import { getJson, postJson, putJson } from './http';

// ─── 열거 — 백엔드와 같은 철자 ───────────────────────────────────────
/** 화면 탭. attention = 사람 볼 것(needs_confirm ∪ check_failed ∪ assemble_failed ∪ job_error 있음). 기본값 */
export const E_BULLETIN_VIEW_FILTERS = ['attention', 'check_failed', 'assemble_failed', 'job_error', 'all'] as const;
export type  EBulletinViewFilter     = typeof E_BULLETIN_VIEW_FILTERS[number];

/** bulletin_doc.status (마이그 326) */
export const E_BULLETIN_STATUSES = [
  'unpacked', 'graded', 'drafted', 'fixed', 'needs_confirm', 'checked_ok', 'check_failed', 'assembled', 'assemble_failed', 'stale',
] as const;
export type  EBulletinStatus = typeof E_BULLETIN_STATUSES[number];

export const E_VENDORS = ['HPE', 'Dell', 'Lenovo'] as const;
export type  EVendor   = typeof E_VENDORS[number];

/** 레코드 한 줄의 type 여섯 (백엔드 E_ASSEMBLED_RECORD_TYPES) */
export const E_ASSEMBLED_RECORD_TYPES = ['doc', 'chassis', 'preconfig_chassis', 'part', 'rule', 'group_note'] as const;
export type  EAssembledRecordType     = typeof E_ASSEMBLED_RECORD_TYPES[number];

const E_NOTE_TYPES   = ['own', 'shared'] as const;                      // own 그 줄 옆 문장 · shared 각주(가리킨 물건마다 복사)
const E_SOURCE_KINDS = ['html', 'pdf', 'price_list'] as const;
const E_CONFIG_TYPES = ['BTO', 'Smart_Choice', 'Smart_Buy'] as const;   // HPE 문서에 실제로 있는 셋

// ─── 스키마 ─────────────────────────────────────────────────────────
/** 문서 한 편의 화면 열. 목록·한 편·recheck 응답이 전부 이 모양 */
export const BulletinDocViewSchema = z.object({
  vendor:         z.enum(E_VENDORS),
  doc_no:         z.number().int(),
  title:          z.string().nullable(),         // 333. html <title>. NULL = 원문에 제목이 없는 옛 조각
  generation:     z.string().nullable(),
  status:         z.enum(E_BULLETIN_STATUSES),
  unsure_rows:    z.number().int().nullable(),   // fixed 이전엔 없다
  check_error:    z.string().nullable(),         // revise 검사가 걸린 이유. "표.줄 — 이유" 줄들
  job_error:      z.string().nullable(),         // 그 편에서 난 마지막 예외
  assemble_error: z.string().nullable(),         // 사람이 적는 버그 칸
  updated_at:     z.string(),
});
/** 목록 한 묶음(페이징). 백엔드 BulletinDocViewList 와 같은 모양. total = 조건에 맞는 전체 편 수(offset 과 무관) */
export const BulletinDocViewListSchema = z.object({
  rows:   z.array(BulletinDocViewSchema),
  total:  z.number().int(),
  limit:  z.number().int(),
  offset: z.number().int(),
});

export type BulletinDocView = z.infer<typeof BulletinDocViewSchema>;
export type BulletinDocViewList = z.infer<typeof BulletinDocViewListSchema>;

// ─── 레코드 — assembled/<번호>.jsonl 한 줄. 백엔드 ASSEMBLED_RECORD_SCHEMA 와 같은 엄격 합집합 ─────
const NoteSchema    = z.strictObject({ type: z.enum(E_NOTE_TYPES), text: z.string() });
const DocSpecSchema = z.strictObject({ section: z.string(), label: z.string(), value: z.string() });
/** 격자 — 축 하나(전압)에 따라 값이 달라지는 항목들. specs(낱값)와 다른 종류. axis 는 축이 열마다 같은 표면 null */
const AssembledGridSchema = z.strictObject({
  axis:    z.strictObject({ name: z.string(), values: z.array(z.string()) }).nullable(),
  by_axis: z.record(z.string(), z.array(z.string())),
});
export const AssembledRecordSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('doc'), doc: z.number().int(), vendor: z.enum(E_VENDORS), source_kind: z.enum(E_SOURCE_KINDS),
    title: z.string(), model: z.string(), version: z.number().nullable(), published_date: z.string().nullable(),
    specs: z.array(DocSpecSchema), notes: z.array(NoteSchema), html_hash: z.string(), revision_hash: z.string(),
  }),
  z.strictObject({
    type: z.literal('chassis'), doc: z.number().int(), sku: z.string(), name: z.string(),
    attrs: z.record(z.string(), z.string()), notes: z.array(NoteSchema),
    table: z.number().int(), row: z.number().int(), column: z.number().int(),
  }),
  z.strictObject({
    type: z.literal('preconfig_chassis'), doc: z.number().int(), sku: z.string(), variants: z.array(z.string()),
    config_type: z.enum(E_CONFIG_TYPES), name: z.string(), attrs: z.record(z.string(), z.string()), notes: z.array(NoteSchema),
    table: z.number().int(), row: z.number().int(), column: z.number().int(),
  }),
  z.strictObject({
    type: z.literal('part'), doc: z.number().int(), pn: z.string(), desc: z.string(), section: z.string(),
    heading_path: z.array(z.string()), specs: z.record(z.string(), z.string()), grid: AssembledGridSchema.nullable(),
    notes: z.array(NoteSchema), applies_to: z.array(z.string()), table: z.number().int(), row: z.number().int(),
  }),
  z.strictObject({
    type: z.literal('rule'), doc: z.number().int(), heading_path: z.array(z.string()), text: z.string(),
    table: z.number().int(), row: z.number().int(),
  }),
  z.strictObject({
    type: z.literal('group_note'), doc: z.number().int(), heading_path: z.array(z.string()), text: z.string(),
    table: z.number().int(), row: z.number().int(),
  }),
]);
export const AssembledRecordsResponseSchema = z.object({ records: z.array(AssembledRecordSchema) });

export type AssembledRecord = z.infer<typeof AssembledRecordSchema>;
export type AssembledGrid   = z.infer<typeof AssembledGridSchema>;
/** 열쇠 (vendor, doc_no) — 한 편을 가리키는 최소 */
export type BulletinDocKey  = Pick<BulletinDocView, 'vendor' | 'doc_no'>;

// ─── 미확정 줄 — 불레틴 미확정 파트 페이지. 백엔드 Bulletin.Types 의 UnsureView · FixProposal · RepairRecord 와 같은 모양 ─────
/** 줄 kind — 초안 · 고침 · 개정본이 같이 쓰는 어휘(백엔드 E_ROW_KINDS) */
export const E_ROW_KINDS = [
  'title', 'heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'figure', 'legend', 'para', 'rule',
  'spec', 'header', 'grid', 'attr', 'sku', 'data', 'note', 'region_key', 'change', 'blank',
] as const;
export type  ERowKind = typeof E_ROW_KINDS[number];

/** 고침에서 kind 만 바꾸는 것 — title(model) · sku(block) 빼고 전부(백엔드 E_FIX_PLAIN_KINDS). 목록 둘을 따로 적지 않는다 — 어긋날 자리가 생긴다 */
export type  EFixPlainKind     = Exclude<ERowKind, 'title' | 'sku'>;
export const E_FIX_PLAIN_KINDS = E_ROW_KINDS.filter((k): k is EFixPlainKind => k !== 'title' && k !== 'sku');

/** sku 줄의 block(백엔드 E_SKU_BLOCKS) */
export const E_SKU_BLOCKS = ['cto', 'BTO', 'Smart_Choice', 'Smart_Buy', 'part'] as const;
export type  ESkuBlock    = typeof E_SKU_BLOCKS[number];

/** 초안 한 줄 — 원문 칸 글. kind 엔 파서가 못 정한 'other' 가 더 있다 */
export const DraftRowSchema = z.strictObject({
  table:   z.number().int(),
  row:     z.number().int(),
  section: z.string(),
  kind:    z.union([z.enum(E_ROW_KINDS), z.literal('other')]),
  label:   z.string(),
  cells:   z.array(z.string()),
});

/** 미확정 줄 하나 — 사람이 정해야 하는 줄. why 는 왜 걸렸나 */
export const UnsureRowSchema = z.strictObject({ table: z.number().int(), row: z.number().int(), why: z.string() });

/** 고침의 알맹이 — 컨펌 때 보내는 몸통. 백엔드 FIX_BODY_SCHEMA 와 같다 */
export const FixBodySchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('title'), model: z.string() }),
  z.strictObject({ kind: z.literal('sku'),   block: z.enum(E_SKU_BLOCKS) }),
  z.strictObject({ kind: z.enum(E_FIX_PLAIN_KINDS as [EFixPlainKind, ...EFixPlainKind[]]) }),
]);

/** 가르기 — 앞글 뒤에 남는 글의 kind(백엔드 E_FIX_RESTS). revise 가 Notes: 에서 한 번 더 갈라 뒤는 note */
export const E_FIX_RESTS = ['rule', 'para'] as const;
export type  EFixRest    = typeof E_FIX_RESTS[number];

/** 가르기 세 칸 — 한 칸에 둘이 붙은 줄을 가를 때. 셋 다 null 이면 안 가름. 백엔드 FixCut 과 같다 */
export const FixCutSchema = z.strictObject({
  text:     z.string().nullable(),
  subtitle: z.string().nullable(),
  rest:     z.enum(E_FIX_RESTS).nullable(),
});

/** 루틴 bulletin-research 의 제안. fix null = 조사했으나 모름(why 에 무엇을 찾아봤는지, evidence 는 []) */
export const FixProposalSchema = z.strictObject({
  table:    z.number().int(),
  rows:     z.array(z.number().int()),
  fix:      FixBodySchema.nullable(),
  why:      z.string(),
  evidence: z.array(z.strictObject({ url: z.string(), quote: z.string() })),
});

/** 미확정 줄 한 줄의 화면 모양 — 서버가 unsure · draft · proposals 를 합쳐 준다 */
export const UnsureViewSchema = z.strictObject({
  unsure:   UnsureRowSchema,
  draft:    DraftRowSchema,
  context:  z.array(DraftRowSchema),   // 같은 표의 앞뒤 5줄(그 줄 빼고). 서버가 자른다
  proposal: FixProposalSchema.nullable(),
});

/** 수리 기록 — assemble_failed 편에 루틴 bulletin-repair 가 낸 PR */
export const RepairRecordSchema = z.strictObject({ pr_url: z.string(), why: z.string() });

export const UnsureViewsResponseSchema = z.object({ rows: z.array(UnsureViewSchema), repair: RepairRecordSchema.nullable() });
export const ConfirmFixResponseSchema  = z.object({ rows: z.array(UnsureViewSchema) });

export type DraftRow     = z.infer<typeof DraftRowSchema>;
export type UnsureRow    = z.infer<typeof UnsureRowSchema>;
export type FixBody      = z.infer<typeof FixBodySchema>;
export type FixCut       = z.infer<typeof FixCutSchema>;
export type FixProposal  = z.infer<typeof FixProposalSchema>;
export type UnsureView   = z.infer<typeof UnsureViewSchema>;
export type RepairRecord = z.infer<typeof RepairRecordSchema>;

// ─── 호출 ────────────────────────────────────────────────────────────
const BASE = '/api/admin/bulletin';

function docPath(key: BulletinDocKey): string {
  return `${BASE}/docs/${key.vendor}/${key.doc_no}`;
}

/** GET /api/admin/bulletin/docs?filter=&limit=&offset= — 탭 하나의 한 묶음(updated_at 최신순). total 은 묶음 밖까지 센 전체 편 수 */
export async function fetchBulletinDocs(
  filter: EBulletinViewFilter,
  limit: number,
  offset: number,
  opts: { signal?: AbortSignal } = {},
): Promise<BulletinDocViewList> {
  return getJson(
    `${BASE}/docs?filter=${filter}&limit=${limit}&offset=${offset}`,
    { signal: opts.signal, schema: BulletinDocViewListSchema },
  );
}

/** GET /api/admin/bulletin/docs/:vendor/:doc_no — 한 편. 없으면 404 → Error */
export async function fetchBulletinDoc(key: BulletinDocKey): Promise<BulletinDocView> {
  return getJson(docPath(key), { schema: BulletinDocViewSchema });
}

/** POST …/recheck — 다시 검사(revise 지금). 검사 뒤의 그 편. check_failed 아니면 409 → Error 메시지 */
export async function recheckBulletinDoc(key: BulletinDocKey): Promise<BulletinDocView> {
  return postJson(`${docPath(key)}/recheck`, {}, BulletinDocViewSchema);
}

/** GET …/records — 한 편의 레코드 전부(파일 순서). 아직 assemble 안 됐으면 404 → Error */
export async function fetchAssembledRecords(key: BulletinDocKey): Promise<AssembledRecord[]> {
  const body = await getJson(`${docPath(key)}/records`, { schema: AssembledRecordsResponseSchema });

  return body.records;
}

/** PUT …/assemble-error — 버그 칸(assemble_error) 하나를 놓는다. 글이면 적기, null 이면 비우기. 적은 뒤의 그 편 */
export async function setAssembleError(key: BulletinDocKey, whyOrNull: string | null): Promise<BulletinDocView> {
  return putJson(`${docPath(key)}/assemble-error`, { why: whyOrNull }, BulletinDocViewSchema);
}

/** GET …/unsure — 미확정 줄 페이지: 그 편의 미확정 줄 전부(table · row 오름차순) + 수리 기록. 초안 없는 편은 404 → Error */
export async function fetchUnsureViews(key: BulletinDocKey): Promise<{ rows: UnsureView[]; repair: RepairRecord | null }> {
  return getJson(`${docPath(key)}/unsure`, { schema: UnsureViewsResponseSchema });
}

/** POST …/unsure/:table/:row/confirm — 그 줄을 fix 로 확정(고쳤으면 고친 값). 서버가 fixes 에 붙이고 unsure 에서 빼 커밋한다. 돌려주는 건 남은 줄.
 *  status 가 needs_confirm · check_failed 아니면 409, 이미 컨펌한 줄이면 404 — 화면은 다시 불러온다 */
export async function confirmFix(key: BulletinDocKey, table: number, row: number, fix: FixBody): Promise<UnsureView[]> {
  const body = await postJson(`${docPath(key)}/unsure/${table}/${row}/confirm`, fix, ConfirmFixResponseSchema);

  return body.rows;
}
